import { createApi } from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import {
  AvanttiReport, EventRevenueReport, PaymentDTO, PlatformRevenueReport, SuspiciousCardOrderDTO,
} from '../../models/payment';
import baseQueryWithReauth from './base';
import { toArgentinaReportBounds } from '../../utils/dates';

const paymentsApi = createApi({
  reducerPath: 'paymentsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['payments'],
  endpoints: (build) => ({
    getPaymentById: build.query<PaymentDTO | null, { paymentId: string }>({
      query: ({ paymentId }) => ({
        url: `/payments/${paymentId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<PaymentDTO>): (PaymentDTO | null) => response.data || null,
    }),
    // El link de pago de Payway no avisa cuando se paga: al volver del
    // formulario se le pide al backend que le pregunte a Payway.
    syncPaywayLinkPayment: build.query<{ payment_status: string | null } | null, { customId: string }>({
      query: ({ customId }) => ({
        url: `/payments/payway-link/sync/${customId}`,
        method: 'POST',
      }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<{ payment_status: string | null }>) => response.data || null,
    }),
    getPaymentByOrderId: build.query<PaymentDTO[] | null, { orderId: string, eventId: string, businessId: string }>({
      query: ({ orderId, eventId, businessId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/payments/order/${orderId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<PaymentDTO[]>): (PaymentDTO[] | null) => response.data || null,
    }),
    getAvanttiPaymentReport: build.query<AvanttiReport | null, { startDate: string, endDate: string }>({
      query: ({ startDate, endDate }) => {
        const bounds = toArgentinaReportBounds(startDate, endDate);
        return {
          url: '/payments/all/report',
          method: 'GET',
          params: {
            start: bounds.start,
            end: bounds.end,
          },
        };
      },
      transformResponse: (response: Response<AvanttiReport>): (AvanttiReport | null) => response.data || null,
      providesTags: ['payments'],
    }),
    getEventRevenueReport: build.query<EventRevenueReport | null, { eventId: string, start?: string, end?: string }>({
      query: ({ eventId, start, end }) => {
        const bounds = start && end ? toArgentinaReportBounds(start, end) : { start, end };
        return {
          url: `/events/${eventId}/payments/reports/revenue-report`,
          method: 'GET',
          params: {
            ...(bounds.start ? { start: bounds.start } : {}),
            ...(bounds.end ? { end: bounds.end } : {}),
          },
        };
      },
      transformResponse: (response: Response<EventRevenueReport>): (EventRevenueReport | null) => response.data || null,
      providesTags: ['payments'],
    }),
    getPlatformRevenueReport: build.query<PlatformRevenueReport | null, { startDate: string, endDate: string }>({
      query: ({ startDate, endDate }) => {
        const bounds = toArgentinaReportBounds(startDate, endDate);
        return {
          url: '/events/all/revenue-report',
          method: 'GET',
          params: {
            start: bounds.start,
            end: bounds.end,
          },
        };
      },
      transformResponse: (response: Response<PlatformRevenueReport>): (PlatformRevenueReport | null) => response.data || null,
      providesTags: ['payments'],
    }),
    getSuspiciousCardOrders: build.query<SuspiciousCardOrderDTO[], void>({
      query: () => ({
        url: '/payments/all/suspicious-orders',
        method: 'GET',
      }),
      transformResponse: (response: Response<SuspiciousCardOrderDTO[]>): SuspiciousCardOrderDTO[] => response.data || [],
      providesTags: ['payments'],
    }),
    getEventPaymentReport: build.query<PaymentDTO[] | null, { eventId: string, businessId: string }>({
      query: ({ eventId, businessId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/payments`,
        method: 'GET',
      }),
      transformResponse: (response: Response<PaymentDTO[]>): (PaymentDTO[] | null) => response.data || null,
      providesTags: ['payments'],
    }),
    getEventPaymentReportXlsx: build.mutation<Blob | null, { eventId: string, businessId: string }>({
      query: ({ eventId, businessId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/payments?xlsx=true`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `detalle-pagos-${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}-${businessId}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
  }),
});

export const {
  useGetPaymentByIdQuery,
  useSyncPaywayLinkPaymentQuery,
  useGetAvanttiPaymentReportQuery,
  useGetEventPaymentReportQuery,
  useGetEventRevenueReportQuery,
  useGetPlatformRevenueReportQuery,
  useGetSuspiciousCardOrdersQuery,
  useGetPaymentByOrderIdQuery,
  useGetEventPaymentReportXlsxMutation,
} = paymentsApi;
export { paymentsApi };
